import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SettingsButton } from "@/Common/Components/Settings/SettingsButton";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { IPendingApprovalInboxModel } from "@/Portfolio/Models/UI/IPendingApprovalInboxModel";

import "./PendingApprovalSettingsContainer.css";

interface IPendingApprovalSettingsContainer {
    Model: IPendingApprovalInboxModel;
    onClick?: any;
    id?: string;
    className?: string;
    style?: any;
}

class PendingApprovalSettingsContainer extends BaseReactComponent<IPendingApprovalSettingsContainer, any> {
    public componentDidMount(): void {
        super.setOverlay("PendingApprovalSettingsContainer", { transparent: true });
    }

    public render(): JSX.Element {
        const settingsclass = `${this.props.className} pending-approval-settings`;
        return (
            <div
                id={this.props.id}
                className={settingsclass}
                style={this.props.style}
                onClick={this.props.onClick}
                data-cy="settingsContainer"
            >
                <table className="f-content">
                    <thead>
                        <tr>
                            <th>
                                <label className="date-label">{cacheStore.GetString("DateLabel").toUpperCase()}</label>
                            </th>
                            {this.props.Model.get("Sites").count() > 1 && (
                                <th>
                                    <CheckboxControl
                                        id="sites_check"
                                        label={cacheStore.GetString("SitesLabel").toUpperCase()}
                                        operation={LocalOperations.Portfolio.PendingApproval_OnSettingsContainerCheckboxChange}
                                        checked={this.props.Model.get("SelectAllSitesChecked")}
                                        target={CheckboxTarget.Myle_Portfolio_PendingApproval_SitesControlCheck}
                                    />
                                </th>
                            )}
                            <th>
                                <CheckboxControl
                                    id="clinicians_check"
                                    label={cacheStore.GetString("CliniciansLabel").toUpperCase()}
                                    operation={LocalOperations.Portfolio.PendingApproval_OnSettingsContainerCheckboxChange}
                                    checked={this.props.Model.get("SelectAllCliniciansChecked")}
                                    target={CheckboxTarget.Myle_Portfolio_PendingApproval_CliniciansControlCheck}
                                />
                            </th>
                            <th>
                                <CheckboxControl
                                    id="status_check"
                                    label={cacheStore.GetString("StatusLabel").toUpperCase()}
                                    operation={LocalOperations.Portfolio.PendingApproval_OnSettingsContainerCheckboxChange}
                                    checked={this.props.Model.get("SelectAllStatusChecked")}
                                    target={CheckboxTarget.Myle_Portfolio_PendingApproval_StatusControlCheck}
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <MultiDateSelector
                                    id="portfolio_pendingapproval_date_selector"
                                    fromMyleOperation={LocalOperations.Portfolio.PendingApproval_SearchSettings_FromDateChanged}
                                    toMyleOperation={LocalOperations.Portfolio.PendingApproval_SearchSettings_ToDateChanged}
                                    fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    from={this.props.Model.get("DateFrom")}
                                    to={this.props.Model.get("DateTo")}
                                />
                            </td>
                            {this.props.Model.get("Sites").count() > 1 && (
                                <td className="sites-column">
                                    <div className="sites-control">
                                        <CheckedListControl
                                            DataSource={this.props.Model.get("Sites")}
                                            Operation={LocalOperations.Portfolio.PendingApproval_OnSettingsContainerCheckboxChange}
                                            MaxTextLength={25}
                                        />
                                        <CheckboxControl
                                            id="sites_onlySelected"
                                            label={cacheStore.GetString("ShowOnlySelectedSiteLabel").toUpperCase()}
                                            operation={LocalOperations.Portfolio.PendingApproval_OnSettingsContainerCheckboxChange}
                                            checked={this.props.Model.get("ShowOnlySelectedSites")}
                                            target={CheckboxTarget.Myle_Portfolio_PendingApproval_OnlySelectedSites}
                                            DivWrap
                                        />
                                    </div>
                                </td>
                            )}
                            <td>
                                <input
                                    className="form-control clinicsearch-text"
                                    type="text"
                                    value={this.props.Model.get("CliniciansSearchText")}
                                    onChange={(e: any) => this.onClinitiansFilterTextChanged(e.target.value)}
                                    placeholder={cacheStore.GetString("SearchLabel")}
                                />
                                <div className="clinician-filter">
                                    <CheckedListControl
                                        DataSource={this.props.Model.get("CliniciansFiltered")}
                                        Operation={LocalOperations.Portfolio.PendingApproval_OnSettingsContainerCheckboxChange}
                                        sort
                                        limitTextWidth
                                        enableLazyRendering
                                    />
                                </div>
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.props.Model.get("Status")}
                                    Operation={LocalOperations.Portfolio.PendingApproval_OnSettingsContainerCheckboxChange}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className="f-footer clearfix">
                    <div className="pull-left">
                        <SettingsButton
                            alternativeIcon
                            rightMargin={5}
                            onClickOperation={LocalOperations.Portfolio.PendingApproval_SearchSettings_ColumnOrderingPopup_Open}
                        />
                    </div>
                    <div className="pull-right">
                        <ButtonControl
                            className="btn btn-default cancel-label"
                            onClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PendingApproval_SearchSettings_CancelClick)}
                            label={cacheStore.GetString("CancelLabel")}
                        />
                        <ButtonControl
                            className="btn btn-primary ok-label"
                            onClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PendingApproval_SearchSettings_OkClick)}
                            label={cacheStore.GetString("Ok_Label")}
                        />
                    </div>
                </div>
            </div>
        );
    }

    public onClinitiansFilterTextChanged(value: string): void {
        sessionStore.dispatch(LocalOperations.Portfolio.PendingApproval_SearchSettings_ClinicianFilterTextChanged, { value });
    }
}

export { PendingApprovalSettingsContainer };
